import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  Mail,
  MessageCircle,
  HelpCircle,
  Shield,
  ChevronDown,
  CheckCircle2,
  Trash2,
  Activity,
  Building2,
} from "lucide-react";
import api from "../services/api";
import { AxiosError } from "axios";

/* ============================================================================
 * Static content — kept colocated so the page is self-contained and easy to
 * tweak without hunting through other modules.
 * ========================================================================== */

const SUPPORT_EMAIL = "team@ealoom.com";
const PRIVACY_EMAIL = "privacy@ealoom.com";
const REPLY_SLA = "We reply within 1 business day, Monday to Friday, Pacific Time.";

const CATEGORIES = [
  { value: "general",         label: "General inquiry" },
  { value: "account",         label: "Account / login issue" },
  { value: "bug_report",      label: "Bug report" },
  { value: "feature_request", label: "Feature request" },
  { value: "partnership",     label: "Partnership / sales" },
  { value: "data_deletion",   label: "Delete my account / data" },
] as const;

type Category = (typeof CATEGORIES)[number]["value"];

interface FaqItem {
  q: string;
  a: string;
}

const DISPATCHER_FAQ: FaqItem[] = [
  {
    q: "I forgot my password. How do I reset it?",
    a: "Click 'Forgot password' on the login screen and enter the email you registered with. We'll send a 6-digit code that's valid for 15 minutes. If the email never arrives, check your spam folder or write to us at team@ealoom.com.",
  },
  {
    q: "How do I invite my dispatchers, accountants, or drivers?",
    a: "From the dispatch console, open Settings → Team. Generate an invite code for each role and share it with your team. Drivers join from the mobile app using the same flow. Web self-registration is reserved for the company owner and dispatchers — back-office roles like accountant and document clerk must be invited from inside the company.",
  },
  {
    q: "How do I change permissions for a team member?",
    a: "Settings → Team → click the user → Roles & Permissions. Permissions are page-scoped (e.g., 'can view Driver Payment', 'can edit Invoice'). The boss role bypasses all checks.",
  },
  {
    q: "How do I export an invoice or driver statement to PDF?",
    a: "Open the invoice or statement and click the PDF icon at the top right. The PDF is generated server-side and downloads to your browser. For Excel exports of the underlying detail rows, use the 'Export' button on the list page.",
  },
  {
    q: "Why is one of my drivers not showing up in the assign dropdown?",
    a: "A driver appears in dispatch only after (1) they accept the company invite from the mobile app, and (2) their profile is marked active. If they signed up but never accepted, the invite is still pending — re-send it from Settings → Team → Pending invites.",
  },
];

const DRIVER_FAQ: FaqItem[] = [
  {
    q: "The app says my GPS location is unavailable.",
    a: "Make sure location permission is set to 'Allow all the time' (Android) or 'Always' (iOS). Background location is required for automatic check-ins. On Android 12+, also enable 'Precise location'. If the issue persists after restarting the app, contact support.",
  },
  {
    q: "My photo upload keeps failing.",
    a: "Photo uploads require an internet connection. The app retries automatically on Wi-Fi — pending photos show a small upload icon. If a photo stays stuck for more than 10 minutes, force-close the app and reopen; uploads will resume from where they left off.",
  },
  {
    q: "I'm not getting push notifications for new tasks.",
    a: "Check that notifications are enabled for EAloom in your phone settings. On Android, also make sure 'Battery optimization' for EAloom is set to 'Unrestricted' — otherwise the system can silently throttle background messages.",
  },
  {
    q: "My task list is empty even though I should have work.",
    a: "Pull down on the task list to refresh. If still empty, log out and back in — this re-syncs your driver profile. If you recently switched companies, your dispatcher needs to assign tasks to you under the new company first.",
  },
  {
    q: "How do I delete my driver account?",
    a: `Send an email from your registered address to ${PRIVACY_EMAIL} with the subject "Delete my account". We'll confirm receipt within 1 business day and complete deletion within 30 days, retaining only the records required by law (tax, payroll). See our Privacy Policy for full details.`,
  },
];

/* ============================================================================
 * Component
 * ========================================================================== */

const Support = () => {
  // Update document title for SEO + browser tab. Cleanup restores previous title
  // so SPA navigation doesn't leak the support title onto other pages.
  useEffect(() => {
    const prev = document.title;
    document.title = "Support — EAloom";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-slate-200 font-sans">
      <Header />
      <main>
        <Hero />
        <ContactCards />
        <ContactForm />
        <FaqSection />
        <AccountDeletion />
        <Resources />
        <SystemStatus />
        <AboutCompany />
      </main>
      <Footer />
    </div>
  );
};

export default Support;

/* ============================================================================
 * Top nav — minimal, just so the user can get back to home / log in.
 * ========================================================================== */

const Header = () => (
  <header className="sticky top-0 z-40 bg-[#0a0a0a]/85 backdrop-blur-md border-b border-white/5">
    <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
      <Link to="/" className="text-white font-black text-lg tracking-tight">
        EAloom
      </Link>
      <nav className="flex items-center gap-6 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
        <Link to="/" className="hover:text-white transition-colors">Home</Link>
        <Link to="/login" className="hover:text-white transition-colors">Log in</Link>
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="px-4 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 transition-colors normal-case tracking-normal"
        >
          Email us
        </a>
      </nav>
    </div>
  </header>
);

/* ============================================================================
 * Hero
 * ========================================================================== */

const Hero = () => (
  <section className="px-6 pt-20 pb-12 max-w-4xl mx-auto text-center">
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs font-bold uppercase tracking-[0.22em] mb-6">
      <HelpCircle className="w-3.5 h-3.5" strokeWidth={2.2} />
      Support Center
    </div>
    <h1 className="text-4xl md:text-6xl font-black tracking-tighter text-white mb-5 leading-[1.05]">
      We're here to <span className="text-emerald-400">help.</span>
    </h1>
    <p className="text-slate-400 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
      Questions, bug reports, account help — pick the fastest channel below.
      {" "}{REPLY_SLA}
    </p>
    <p className="text-slate-500 text-xs mt-4 tracking-wide">
      Support provided by{" "}
      <span className="text-emerald-400 font-semibold">Lambda Flow LLC</span>, the company behind EAloom.
    </p>
  </section>
);

/* ============================================================================
 * Contact cards — Email + Form anchor
 * ========================================================================== */

const ContactCards = () => (
  <section className="px-6 pb-12 max-w-4xl mx-auto">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <a
        href={`mailto:${SUPPORT_EMAIL}`}
        className="group block rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-emerald-500/30 transition-all p-6"
      >
        <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-4 group-hover:bg-emerald-500/20 transition-colors">
          <Mail className="w-5 h-5 text-emerald-400" strokeWidth={2} />
        </div>
        <h3 className="text-white font-bold text-sm mb-1">Email us directly</h3>
        <p className="text-emerald-400 text-sm font-mono">{SUPPORT_EMAIL}</p>
        <p className="text-slate-500 text-xs mt-2 leading-relaxed">
          Best for account-specific questions where you can include screenshots or attachments.
        </p>
      </a>
      <a
        href="#contact-form"
        className="group block rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-emerald-500/30 transition-all p-6"
      >
        <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-4 group-hover:bg-emerald-500/20 transition-colors">
          <MessageCircle className="w-5 h-5 text-emerald-400" strokeWidth={2} />
        </div>
        <h3 className="text-white font-bold text-sm mb-1">Send a message</h3>
        <p className="text-slate-300 text-sm">Use the contact form below</p>
        <p className="text-slate-500 text-xs mt-2 leading-relaxed">
          Categorize your request so it routes to the right team. No login required.
        </p>
      </a>
    </div>
  </section>
);

/* ============================================================================
 * Contact form
 * ========================================================================== */

const ContactForm = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<Category>("general");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;

    const form = e.currentTarget;
    const fd = new FormData(form);
    const payload = {
      name:     String(fd.get("name") || "").trim(),
      email:    String(fd.get("email") || "").trim(),
      company:  String(fd.get("company") || "").trim() || undefined,
      phone:    String(fd.get("phone") || "").trim() || undefined,
      category,
      subject:  String(fd.get("subject") || "").trim(),
      message:  String(fd.get("message") || "").trim(),
      // honeypot — hidden field, should always be empty for real users
      website:  String(fd.get("website") || "").trim(),
    };

    if (!payload.name || !payload.email || !payload.subject || !payload.message) {
      setError("Please fill in all required fields.");
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      await api.post("/public/support-contact", payload);
      setSubmitted(true);
      form.reset();
      setCategory("general");
    } catch (err) {
      const axiosErr = err as AxiosError<{ message?: string }>;
      setError(
        axiosErr.response?.data?.message ||
          "Could not submit your request. Please check your connection and try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact-form" className="px-6 py-16 border-t border-white/5">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-white mb-3">
            Send a message
          </h2>
          <p className="text-slate-400 text-sm leading-relaxed">
            Everything stays between you and our team. We never sell or share contact info.
          </p>
        </div>

        {submitted ? (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center">
            <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" strokeWidth={1.8} />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Message received</h3>
            <p className="text-slate-400 text-sm leading-relaxed">
              Thanks — we'll get back to you at the email you provided.<br />
              {REPLY_SLA}
            </p>
            <button
              type="button"
              onClick={() => setSubmitted(false)}
              className="mt-6 px-6 py-2.5 bg-white/5 border border-white/10 hover:border-white/20 text-slate-300 text-xs uppercase tracking-[0.2em] font-bold rounded-lg transition-all"
            >
              Send another
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput name="name" placeholder="Your name *" required maxLength={120} disabled={submitting} />
              <FormInput name="email" type="email" placeholder="Work email *" required maxLength={254} disabled={submitting} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput name="company" placeholder="Company (optional)" maxLength={160} disabled={submitting} />
              <FormInput name="phone" type="tel" placeholder="Phone (optional)" maxLength={40} disabled={submitting} />
            </div>

            <div className="relative">
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as Category)}
                disabled={submitting}
                aria-label="Category"
                className="w-full appearance-none bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-emerald-500 focus:bg-white/[0.07] transition-colors disabled:opacity-60 pr-10"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value} className="bg-[#0d0d0d]">
                    {c.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>

            <FormInput name="subject" placeholder="Subject *" required maxLength={200} disabled={submitting} />

            <textarea
              name="message"
              required
              maxLength={5000}
              disabled={submitting}
              placeholder="Tell us what's going on. Include any error messages, dates, or task IDs if relevant. *"
              rows={6}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-500 focus:bg-white/[0.07] transition-colors disabled:opacity-60 resize-y"
            />

            {/* Honeypot: hidden from real users via aria + offscreen positioning. */}
            <div aria-hidden="true" className="absolute left-[-9999px] w-px h-px overflow-hidden">
              <label>
                Leave this field empty
                <input type="text" name="website" tabIndex={-1} autoComplete="off" />
              </label>
            </div>

            {error && (
              <p role="alert" className="text-rose-400 text-xs leading-relaxed">{error}</p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-black py-3 rounded-lg text-sm uppercase tracking-[0.25em] transition-all active:scale-[0.98] mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Sending…" : "Send Message"}
            </button>

            <p className="text-slate-500 text-xs text-center leading-relaxed pt-2">
              By submitting, you agree to our{" "}
              <Link to="/" className="text-slate-400 hover:text-emerald-400 transition-colors">Privacy Policy</Link>.
              We use your contact details only to respond to your request.
            </p>
          </form>
        )}
      </div>
    </section>
  );
};

interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {}

const FormInput = (props: FormInputProps) => (
  <input
    type="text"
    {...props}
    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-500 focus:bg-white/[0.07] transition-colors disabled:opacity-60"
  />
);

/* ============================================================================
 * FAQ
 * ========================================================================== */

const FaqSection = () => (
  <section className="px-6 py-16 border-t border-white/5">
    <div className="max-w-5xl mx-auto">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-white mb-3">
          Frequently asked questions
        </h2>
        <p className="text-slate-400 text-sm">
          Pick your side — dispatchers and drivers see different things.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FaqColumn title="For Dispatchers & Office" items={DISPATCHER_FAQ} />
        <FaqColumn title="For Drivers (Mobile App)" items={DRIVER_FAQ} />
      </div>
    </div>
  </section>
);

const FaqColumn = ({ title, items }: { title: string; items: FaqItem[] }) => (
  <div>
    <h3 className="text-xs uppercase tracking-[0.22em] text-emerald-400 font-bold mb-4">
      {title}
    </h3>
    <div className="space-y-2">
      {items.map((item, i) => (
        <FaqRow key={i} item={item} />
      ))}
    </div>
  </div>
);

const FaqRow = ({ item }: { item: FaqItem }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 text-left px-4 py-3 hover:bg-white/[0.03] transition-colors"
      >
        <span className="text-sm font-semibold text-white">{item.q}</span>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-4 pb-4 -mt-1 text-sm text-slate-400 leading-relaxed">
          {item.a}
        </div>
      )}
    </div>
  );
};

/* ============================================================================
 * Account deletion — Google Play / App Store compliance section
 * ========================================================================== */

const AccountDeletion = () => (
  <section id="delete-account" className="px-6 py-16 border-t border-white/5">
    <div className="max-w-3xl mx-auto">
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-10 h-10 rounded-lg bg-rose-500/10 border border-rose-500/20 flex items-center justify-center shrink-0">
            <Trash2 className="w-5 h-5 text-rose-400" strokeWidth={2} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white mb-1">Delete your account &amp; data</h2>
            <p className="text-slate-400 text-sm">
              You can request full deletion of your EAloom account at any time.
            </p>
          </div>
        </div>

        <ol className="space-y-3 text-sm text-slate-300 leading-relaxed list-decimal list-inside pl-1">
          <li>
            Email{" "}
            <a href={`mailto:${PRIVACY_EMAIL}?subject=Delete%20my%20account`} className="text-emerald-400 hover:text-emerald-300">
              {PRIVACY_EMAIL}
            </a>{" "}
            from the address registered to your account.
          </li>
          <li>Include the subject line <span className="font-mono text-white">Delete my account</span> and your registered phone number (drivers) or company name (dispatchers).</li>
          <li>We'll confirm receipt within <strong className="text-white">1 business day</strong> and complete deletion within <strong className="text-white">30 days</strong>.</li>
        </ol>

        <div className="mt-6 p-4 rounded-lg bg-amber-500/5 border border-amber-500/20">
          <p className="text-xs text-amber-200/90 leading-relaxed">
            <strong className="text-amber-300">What we keep:</strong> US tax, payroll, and dispatch records that we are legally required to retain (typically 7 years). These records are anonymized where possible and are not used for any other purpose.
          </p>
        </div>
      </div>
    </div>
  </section>
);

/* ============================================================================
 * Resources / quick links
 * ========================================================================== */

const Resources = () => (
  <section className="px-6 py-16 border-t border-white/5">
    <div className="max-w-4xl mx-auto">
      <h2 className="text-center text-3xl md:text-4xl font-black tracking-tighter text-white mb-10">
        Resources
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ResourceCard
          icon={<Shield className="w-5 h-5 text-emerald-400" strokeWidth={2} />}
          title="Privacy Policy"
          description="How we collect, store, and protect your data."
          href="/"
        />
        <ResourceCard
          icon={<Shield className="w-5 h-5 text-emerald-400" strokeWidth={2} />}
          title="Terms of Service"
          description="Your agreement with us for using the platform."
          href="/"
        />
        <ResourceCard
          icon={<Mail className="w-5 h-5 text-emerald-400" strokeWidth={2} />}
          title="Sales & Demo"
          description="Talk to us about a demo or trial."
          href="/#demo"
        />
      </div>
    </div>
  </section>
);

const ResourceCard = ({
  icon,
  title,
  description,
  href,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  href: string;
}) => (
  <a
    href={href}
    className="block rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-emerald-500/30 transition-all p-5"
  >
    <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-3">
      {icon}
    </div>
    <h3 className="text-white font-bold text-sm mb-1">{title}</h3>
    <p className="text-slate-500 text-xs leading-relaxed">{description}</p>
  </a>
);

/* ============================================================================
 * System status — placeholder until a real status page is wired up
 * ========================================================================== */

const SystemStatus = () => (
  <section className="px-6 py-12 border-t border-white/5">
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Activity className="w-5 h-5 text-emerald-400" strokeWidth={2} />
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          </div>
          <div>
            <p className="text-white text-sm font-semibold">All systems operational</p>
            <p className="text-slate-500 text-xs">Dispatch, mobile, email, and webhooks are healthy.</p>
          </div>
        </div>
        <span className="text-xs text-slate-500 font-mono">
          {new Date().toISOString().slice(0, 10)}
        </span>
      </div>
    </div>
  </section>
);

/* ============================================================================
 * About EAloom — explicit corporate attribution to Lambda Flow LLC.
 * Separate section above the footer so the relationship is unmistakable, not
 * just a fine-print line at the bottom of the page.
 * ========================================================================== */

const AboutCompany = () => (
  <section id="about" className="px-6 py-16 border-t border-white/5">
    <div className="max-w-3xl mx-auto">
      <div className="rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/[0.04] to-white/[0.02] p-8 md:p-10">
        <div className="flex items-start gap-4 mb-5">
          <div className="w-11 h-11 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shrink-0">
            <Building2 className="w-5 h-5 text-emerald-400" strokeWidth={2} />
          </div>
          <div>
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-white leading-tight">
              About EAloom
            </h2>
            <p className="text-xs uppercase tracking-[0.22em] text-emerald-400 font-bold mt-1">
              A Lambda Flow LLC product
            </p>
          </div>
        </div>

        <p className="text-slate-300 text-base leading-relaxed">
          <strong className="text-white">EAloom</strong> is a product of{" "}
          <strong className="text-white">Lambda Flow LLC</strong>, a United States-registered
          limited liability company. All services on this site — including the dispatch console,
          mobile driver app, billing, and this support page — are owned and operated by
          Lambda Flow LLC.
        </p>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg bg-white/[0.03] border border-white/10 px-4 py-3">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Legal entity</div>
            <div className="text-white font-semibold">Lambda Flow LLC</div>
          </div>
          <div className="rounded-lg bg-white/[0.03] border border-white/10 px-4 py-3">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Product</div>
            <div className="text-white font-semibold">EAloom — ealoom.com</div>
          </div>
          <div className="rounded-lg bg-white/[0.03] border border-white/10 px-4 py-3">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">General contact</div>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-emerald-400 font-mono text-sm hover:text-emerald-300">
              {SUPPORT_EMAIL}
            </a>
          </div>
          <div className="rounded-lg bg-white/[0.03] border border-white/10 px-4 py-3">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Privacy contact</div>
            <a href={`mailto:${PRIVACY_EMAIL}`} className="text-emerald-400 font-mono text-sm hover:text-emerald-300">
              {PRIVACY_EMAIL}
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ============================================================================
 * Footer — Lambda Flow LLC attribution (mirrors Home.tsx but emphasized).
 * ========================================================================== */

const Footer = () => (
  <footer className="py-12 px-6 border-t border-white/5 text-center">
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-center gap-6 text-xs text-slate-500 mb-5">
        <Link to="/" className="hover:text-emerald-400 transition-colors">Home</Link>
        <Link to="/login" className="hover:text-emerald-400 transition-colors">Log in</Link>
        <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-emerald-400 transition-colors">{SUPPORT_EMAIL}</a>
        <a href="#about" className="hover:text-emerald-400 transition-colors">About</a>
      </div>

      <div className="text-sm text-slate-300 font-semibold leading-relaxed">
        EAloom is a product of{" "}
        <span className="text-emerald-400 font-bold">Lambda Flow LLC</span>.
      </div>
      <div className="text-xs text-slate-500 font-medium leading-relaxed mt-2">
        &copy; {new Date().getFullYear()} Lambda Flow LLC. All rights reserved.
      </div>
    </div>
  </footer>
);
